import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, Plus, Rows, SquaresFour, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { mktApi, errMsg } from './marketingApi';
import { CustomerPicker, StatusBadge, STATUS_LABELS, yen, jd } from './mktShared';
import OrderFormDialog from './OrderFormDialog';
import OrdersKanban from './OrdersKanban';
import { todayJst } from '@/utils/jstDate';

const FILTER_CHIPS = ['draft', 'quoted', 'approval_pending', 'approved', 'rejected',
  'sent_to_seizo', 'in_production', 'qc_pending', 'qc_passed', 'ready_to_ship',
  'partially_shipped', 'completion_pending', 'completed', 'cancelled'];

export default function Orders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [customerId, setCustomerId] = useState(null);
  const [statuses, setStatuses] = useState(() => (searchParams.get('status') || '').split(',').filter((s) => FILTER_CHIPS.includes(s)));
  const [overdueOnly, setOverdueOnly] = useState(() => searchParams.get('overdue') === '1');
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem('mkt_orders_view') || 'list');
  const switchView = (v) => { setView(v); localStorage.setItem('mkt_orders_view', v); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (customerId) params.customer_id = customerId;
      if (statuses.length) params.status = statuses.join(',');
      if (dueFrom) params.due_from = dueFrom;
      if (dueTo) params.due_to = dueTo;
      setOrders(await mktApi.orders(params));
    } catch (e) { toast.error(errMsg(e, '受注の取得に失敗しました')); }
    finally { setLoading(false); }
  }, [q, customerId, statuses, dueFrom, dueTo]);

  useEffect(() => {
    const tm = setTimeout(load, 250);
    return () => clearTimeout(tm);
  }, [load]);

  useEffect(() => { mktApi.customers().then(setCustomers).catch(() => {}); }, []);

  const today = useMemo(() => todayJst(), []);
  const isOverdue = useCallback((o) => o.due_date && o.due_date < today &&
    !['shipped', 'completion_pending', 'completed', 'cancelled'].includes(o.status), [today]);
  const shown = useMemo(() => (overdueOnly ? orders.filter(isOverdue) : orders), [orders, overdueOnly, isOverdue]);

  const toggleStatus = (s) => setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <div className="space-y-4" data-testid="mkt-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">受注一覧</h1>
          <p className="text-xs text-zinc-500 mt-0.5">見積作成から売上確定までの営業受注を管理します</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-zinc-200 bg-white p-0.5">
            <button type="button" onClick={() => switchView('list')} data-testid="mkt-orders-view-list"
              className={`rounded-md px-2 py-1.5 transition-colors ${view === 'list' ? 'bg-fuchsia-50 text-fuchsia-700' : 'text-zinc-400 hover:text-zinc-600'}`} title="リスト表示">
              <Rows size={15} weight={view === 'list' ? 'bold' : 'regular'} />
            </button>
            <button type="button" onClick={() => switchView('kanban')} data-testid="mkt-orders-view-kanban"
              className={`rounded-md px-2 py-1.5 transition-colors ${view === 'kanban' ? 'bg-fuchsia-50 text-fuchsia-700' : 'text-zinc-400 hover:text-zinc-600'}`} title="カンバン表示">
              <SquaresFour size={15} weight={view === 'kanban' ? 'bold' : 'regular'} />
            </button>
          </div>
          <Button onClick={() => setFormOpen(true)} data-testid="mkt-order-create-btn"
            className="bg-teal-700 hover:bg-teal-800">
            <Plus size={15} className="mr-1.5" /> 新規受注
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white p-3 space-y-2.5">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="受注番号・件名・客先・品名で検索"
              className="pl-8 h-9" data-testid="mkt-orders-search" />
          </div>
          <div className="w-56"><CustomerPicker customers={customers} value={customerId} onChange={setCustomerId} allowClear placeholder="客先で絞り込み" testid="mkt-orders-customer-filter" /></div>
          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
            <span>納期</span>
            <Input type="date" value={dueFrom} onChange={(e) => setDueFrom(e.target.value)} className="h-9 w-36" data-testid="mkt-orders-due-from" />
            <span>〜</span>
            <Input type="date" value={dueTo} onChange={(e) => setDueTo(e.target.value)} className="h-9 w-36" data-testid="mkt-orders-due-to" />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setOverdueOnly((v) => !v)} data-testid="mkt-orders-filter-overdue"
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
              overdueOnly ? 'border-red-400 bg-red-50 text-red-600' : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50'}`}>
            納期超過のみ
          </button>
          {FILTER_CHIPS.map((s) => (
            <button key={s} type="button" onClick={() => toggleStatus(s)} data-testid={`mkt-orders-filter-${s}`}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                statuses.includes(s) ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700' : 'border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50'}`}>
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {view === 'kanban' ? (
        <OrdersKanban orders={shown} loading={loading} isOverdue={isOverdue}
          onOpen={(oid) => navigate(`/marketing/orders/${oid}`)} />
      ) : (
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[11px] uppercase tracking-wide text-zinc-400">
                <th className="px-4 py-2.5 font-medium">受注番号</th>
                <th className="px-3 py-2.5 font-medium">件名 / 客先</th>
                <th className="px-3 py-2.5 font-medium">納期</th>
                <th className="px-3 py-2.5 font-medium text-right">金額(税込)</th>
                <th className="px-3 py-2.5 font-medium">ステータス</th>
                <th className="px-3 py-2.5 font-medium">担当</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-zinc-400">読み込み中…</td></tr>
              )}
              {!loading && shown.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-zinc-400" data-testid="mkt-orders-empty">受注がありません。「新規受注」から作成してください</td></tr>
              )}
              {!loading && shown.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/marketing/orders/${o.id}`)} data-testid={`mkt-order-row-${o.id}`}
                  className="cursor-pointer border-b border-zinc-50 hover:bg-fuchsia-50/40 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-fuchsia-700">{o.order_no}</td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-zinc-900 truncate max-w-[280px]">{o.title || '（件名なし）'}</div>
                    <div className="text-[11px] text-zinc-500"><span data-no-translate>{o.customer_name}</span><span className="ml-2 text-zinc-300">明細 {(o.lines || []).length}件</span></div>
                  </td>
                  <td className={`px-3 py-3 text-xs tabular-nums ${isOverdue(o) ? 'text-red-600 font-semibold' : 'text-zinc-600'}`}>
                    {jd(o.due_date)}{isOverdue(o) && <WarningCircle size={13} weight="fill" className="inline ml-1 -mt-0.5" />}
                  </td>
                  <td className="px-3 py-3 text-right text-xs tabular-nums text-zinc-700">{yen(o.total)}</td>
                  <td className="px-3 py-3"><StatusBadge status={o.status} label={o.status_label} /></td>
                  <td className="px-3 py-3 text-xs text-zinc-500">{o.sales_rep_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <OrderFormDialog open={formOpen} onOpenChange={setFormOpen} customers={customers}
        onSaved={(o) => { setFormOpen(false); navigate(`/marketing/orders/${o.id}`); }} />
    </div>
  );
}
